/**
 * Checks if a step has invalid approvers (users who don't have roles in the company)
 * @param step - The step object to check
 * @param companyUserMap - Map of user IDs to company user objects
 * @returns boolean - true if the step has invalid approvers, false otherwise
 */
export const hasStepInvalidApprovers = (step: any, companyUserMap: any): boolean => {
    if (!step.rules || !Array.isArray(step.rules)) {
        return false;
    }

    return step.rules.some(stepRule => {
        if (!stepRule.action || !companyUserMap) {
            return false;
        }

        // Check users in stepRule.action.users
        if (stepRule.action.users && Array.isArray(stepRule.action.users)) {
            const hasInvalidUsers = stepRule.action.users.some((user: any) => {
                const companyUser = companyUserMap[user];
                return companyUser && companyUser.hasRoleInCompany === false;
            });
            if (hasInvalidUsers) return true;
        }

        // Check users in stepRule.action.exceptions
        if (stepRule.action.exceptions && Array.isArray(stepRule.action.exceptions)) {
            const hasInvalidExceptions = stepRule.action.exceptions.some((exception: any) => {
                // Check exception.users (array)
                if (exception.users && Array.isArray(exception.users)) {
                    return exception.users.some((user: any) => {
                        const companyUser = companyUserMap[user];
                        return companyUser && companyUser.hasRoleInCompany === false;
                    });
                }

                return false;
            });
            if (hasInvalidExceptions) return true;
        }

        return false;
    });
};

/**
 * Checks if a rule has invalid approvers (users who don't have roles in the company)
 * @param rule - The rule object to check (should have companyUserMap property)
 * @returns boolean - true if the rule has invalid approvers, false otherwise
 */
export const hasInvalidApprovers = (rule: any): boolean => {
    if (!rule.steps || !Array.isArray(rule.steps)) {
        return false;
    }

    // Check for invalid approvers in steps
    const hasInvalidStepApprovers = rule.steps.some(step => hasStepInvalidApprovers(step, rule.companyUserMap));

    // Check for invalid fallback users in twoPersonsRule only if enabled
    const hasInvalidFallback = rule.twoPersonsRule.enabled
        ? hasInvalidTwoPersonsRuleUsers(rule.twoPersonsRule.fallbackUsers, rule.companyUserMap)
        : false;

    return hasInvalidStepApprovers || hasInvalidFallback;
};

/**
 * Checks if fallback users are invalid (users who don't have roles in the company)
 * @param fallbackUsers - Array of fallback user IDs to check
 * @param companyUserMap - Map of user IDs to company user objects
 * @returns boolean - true if any fallback user is invalid, false otherwise
 */
export const hasInvalidTwoPersonsRuleUsers = (fallbackUsers: any[], companyUserMap: any): boolean => {
    if (!fallbackUsers || !Array.isArray(fallbackUsers) || !companyUserMap) {
        return false;
    }

    return fallbackUsers.some((userId: any) => {
        const companyUser = companyUserMap[userId];
        return companyUser && companyUser.hasRoleInCompany === false;
    });
};
